import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { fetchBarometer, fetchTrend, refreshBarometer } from "../../lib/api";
import { TrendChart, TrendGrid } from "../TrendChart";
import { timeAgo } from "../../lib/tone";
import { NationalCard, SourceBadge } from "../NationalCard";
import { CanadaTileMap } from "../CanadaTileMap";
import { ProvinceGaugeList } from "../ProvinceGaugeList";
import { CategoryBreakdown } from "../CategoryBreakdown";
import { HappinessComparison } from "../HappinessComparison";
import { MethodologyNote } from "../MethodologyNote";
import { ScoreGauge, TonePill } from "../ScoreGauge";
import { SentimentIndex } from "./SentimentIndex";
import { WeeklyDigest } from "./WeeklyDigest";

export const SentimentView = () => {
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(() => fetchBarometer(period).then(setData).catch(() => toast.error("Couldn't load news tone")), [period]);
  const [trend, setTrend] = useState(null);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { fetchTrend().then(setTrend).catch(() => {}); }, [refreshKey]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await refreshBarometer();
      await load();
      setRefreshKey((k) => k + 1);
      toast.success(`Feeds refreshed · ${r.refreshed} headlines read`);
    } catch { toast.error("Refresh failed — please try again shortly"); }
    setRefreshing(false);
  };

  const sel = data && selected ? data.provinces.find((p) => p.code === selected) : null;
  const focus = sel || data?.national;
  const periodLabel = period === "day" ? "Last 24 hours" : "Last 7 days";

  return (
    <div data-testid="sentiment-view">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <SourceBadge national={data?.national} />
          <h2 className="mt-5 font-serif text-3xl tracking-tight text-slate-900">News Tone</h2>
          <p className="mt-3 max-w-2xl text-base text-slate-600">The balance of positive and negative community news across Canada, read from live public feeds.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-full border border-slate-200 bg-white p-1" role="tablist">
            {[["day", "Today"], ["week", "This week"]].map(([v, l]) => (
              <button key={v} role="tab" aria-selected={period === v} onClick={() => setPeriod(v)} data-testid={`period-toggle-${v}`}
                className={`rounded-full px-4 py-1.5 text-sm transition-colors ${period === v ? "bg-sky-900 text-white" : "text-slate-600 hover:text-sky-900"}`}>{l}</button>
            ))}
          </div>
          <button onClick={refresh} disabled={refreshing} data-testid="refresh-sentiment-feed-btn" aria-label="Refresh feeds"
            className="rounded-full border border-slate-200 bg-white p-2.5 text-slate-600 transition-colors hover:text-sky-900 disabled:opacity-50">
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>
      </div>
      {data?.last_refresh && <p className="mt-3 text-xs text-slate-500" data-testid="last-refresh">Feeds last read {timeAgo(data.last_refresh)}</p>}

      <SentimentIndex provinces={data?.provinces} />
      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.6fr]">
        <NationalCard national={data?.national} periodLabel={periodLabel} />
        <div className="min-w-0 rounded-3xl border border-slate-200 bg-white p-5 sm:p-6">
          {data ? <CanadaTileMap provinces={data.provinces} selected={selected} onSelect={setSelected} /> : <div className="h-64 animate-pulse rounded-xl bg-slate-50" />}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_1fr]">
        <div className="rounded-3xl border border-slate-200 bg-white p-4 sm:p-6">
          <h2 className="px-3 pb-2 font-heading text-lg font-semibold text-slate-900">By province & territory</h2>
          {data && <ProvinceGaugeList provinces={data.provinces} selected={selected} onSelect={setSelected} />}
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="focus-panel">
          {focus && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-heading text-lg font-semibold text-slate-900" data-testid="focus-panel-title">{sel ? sel.name : "Canada"} · what's moving the score</h2>
                <TonePill score={focus.score} />
              </div>
              <div className="mt-4 flex items-center gap-4">
                <span className="font-serif text-4xl text-slate-900" data-testid="focus-panel-score">{Math.round(focus.score)}</span>
                <ScoreGauge score={focus.score} />
              </div>
              <p className="mt-3 text-xs text-slate-500">{focus.article_count} headlines · {focus.mainstream_count} mainstream · {focus.independent_count} independent</p>
              {sel && <button onClick={() => setSelected(null)} className="mt-2 text-xs text-sky-800 underline" data-testid="clear-province-btn">Back to national view</button>}
              <div className="mt-6"><CategoryBreakdown data={focus} meta={data.category_meta} /></div>
            </>
          )}
        </div>
      </div>

      <WeeklyDigest data={data} trend={trend} />

      <div className="mt-6"><MethodologyNote /></div>

      <section className="mt-16" data-testid="trend-section">
        <p className="text-xs font-semibold uppercase tracking-widest text-sky-800">Mood over time</p>
        <h2 className="mt-3 font-serif text-3xl tracking-tight text-slate-900">30-day trend {sel ? `· ${sel.name} vs Canada` : "· Canada"}</h2>
        <p className="mt-2 text-sm text-slate-500">Each point is a rolling {trend?.window_days || 3}-day score. Days with fewer than {trend?.min_articles || 5} stories are left blank, and history fills in as feeds are read each day.</p>
        <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-4 sm:p-6">
          <div className="mb-3 flex gap-4 text-xs text-slate-600">
            <span className="flex items-center gap-1.5"><span className="h-0.5 w-4 bg-sky-900" />Canada</span>
            {sel && <span className="flex items-center gap-1.5"><span className="h-0.5 w-4 bg-emerald-500" />{sel.name}</span>}
          </div>
          <TrendChart trend={trend} code={selected} />
        </div>
        <div className="mt-4"><TrendGrid trend={trend} selected={selected} onSelect={setSelected} /></div>
      </section>

      <div className="mt-20">
        <p className="text-xs font-semibold uppercase tracking-widest text-sky-800">Context</p>
        <h2 className="mb-8 mt-3 font-serif text-3xl tracking-tight text-slate-900">How Canada ranks globally on happiness & life satisfaction</h2>
        <HappinessComparison />
      </div>
    </div>
  );
};
